// src/components/antenna/antenna-run-selector.tsx
import { useEffect, useRef, useState } from "react";
import { History, ChevronDown, Check, Loader2, User } from "lucide-react";
import type { MonitoringHistoryItem } from "@/app/services/brand/monitoring-history-api";

function runLabel(run: MonitoringHistoryItem): string {
  if (!run.createdAt) return `Run ${run.id}`;
  const d = new Date(run.createdAt);
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

const HOVER_BG = "var(--muted)";

/**
 * Run-history switcher for the Brand Monitoring (antenna) dashboard. Lets the
 * user load any previous monitoring run for the brand. The newest run is tagged
 * "Latest". With a single run it renders as a static label (no dropdown).
 */
export function AntennaRunSelector({
  runs,
  selectedRunId,
  onSelect,
  loading = false,
}: {
  runs: MonitoringHistoryItem[];
  selectedRunId: number | null;
  onSelect: (runId: number) => void;
  loading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (loading && runs.length === 0) {
    return (
      <div className="flex items-center gap-2" style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)" }}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading runs…
      </div>
    );
  }
  if (runs.length === 0) return null;

  const latestId = runs[0]?.id ?? null; // history is newest-first
  const selected = runs.find((r) => r.id === selectedRunId) ?? runs[0];
  const isLatest = (id: number | null) => id != null && id === latestId;

  // Single run: static label, no dropdown.
  if (runs.length <= 1) {
    return (
      <div className="flex items-center gap-2" style={{ fontSize: "var(--text-sm)" }}>
        <History className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--muted-foreground)" }} />
        <span style={{ fontWeight: 600, color: "var(--foreground)" }}>{runLabel(selected)}</span>
        <LatestPill show={isLatest(selected.id)} />
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2"
        style={{
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: "6px 10px",
          backgroundColor: "var(--card)",
          cursor: "pointer",
          fontSize: "var(--text-sm)",
          color: "var(--foreground)",
        }}
        title="View a previous monitoring run"
      >
        <History className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--muted-foreground)" }} />
        <span style={{ fontWeight: 600 }}>{runLabel(selected)}</span>
        <LatestPill show={isLatest(selected.id)} />
        <ChevronDown
          className="w-3.5 h-3.5"
          style={{ color: "var(--muted-foreground)", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute z-50"
          style={{
            top: "calc(100% + 4px)", left: 0, minWidth: 260,
            backgroundColor: "var(--card)", border: "1px solid var(--border)",
            borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: 4,
            maxHeight: 320, overflowY: "auto",
          }}
        >
          {runs.map((run) => {
            const isSel = run.id === selectedRunId;
            return (
              <button
                key={run.id}
                type="button"
                role="option"
                aria-selected={isSel}
                onClick={() => { onSelect(run.id); setOpen(false); }}
                className="w-full flex items-center justify-between gap-3"
                style={{
                  textAlign: "left", border: "none",
                  backgroundColor: isSel ? HOVER_BG : "transparent",
                  borderRadius: 7, padding: "8px 10px", cursor: "pointer",
                }}
                onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.backgroundColor = HOVER_BG; }}
                onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.backgroundColor = "transparent"; }}
              >
                <span className="flex flex-col min-w-0" style={{ gap: 2 }}>
                  <span className="flex items-center gap-2 min-w-0">
                    <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
                      {runLabel(run)}
                    </span>
                    <LatestPill show={isLatest(run.id)} />
                  </span>
                  <span className="flex items-center gap-1.5 min-w-0" style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                    {run.username && (
                      <>
                        <User className="w-3 h-3 shrink-0" />
                        <span className="truncate">{run.username}</span>
                      </>
                    )}
                    {run.status && run.status !== "completed" && run.status !== "complete" && (
                      <span>· {run.status}</span>
                    )}
                  </span>
                </span>
                {isSel && <Check className="w-4 h-4 shrink-0" style={{ color: "var(--foreground)" }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LatestPill({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span
      style={{
        fontSize: 10, fontWeight: 700, color: "var(--alpha-teal)",
        backgroundColor: "var(--alpha-teal-10)", border: "1px solid var(--alpha-teal-25, var(--border))",
        borderRadius: 9999, padding: "1px 7px", lineHeight: 1.4, whiteSpace: "nowrap",
      }}
    >
      Latest
    </span>
  );
}
